/**
 * Contract CSF (Customer Statement Field) scope — founder pattern
 * Plain Label — Value under cluster heads. Not a BOM.
 */

import { asArray } from '@/lib/as-array';
import { paperCsfLabel, splitPaperStatementLabel } from '@/lib/paper-display';

export type CsfStatement = {
  scfCode?: string | null;
  groupLabel?: string | null;
  text: string;
  source?: string | null;
};

export type CsfField = {
  scfCode: string;
  scfLabel: string;
  sourceLabel?: string | null;
  contractLabel?: string | null;
  groupLabel?: string | null;
  answerValue?: unknown;
  answerDisplay?: string | null;
  contractVisibility?: string | null;
};

export type CsfRow = {
  n: number;
  label: string | null;
  value: string;
};

export type CsfCluster = {
  groupLabel: string;
  rows: CsfRow[];
};

export type CsfBlock = {
  n: number;
  heading: string;
  clusters: CsfCluster[];
};

function asText(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value).trim();
  return text.length ? text : null;
}

export function freezeStatements(raw: unknown): CsfStatement[] {
  const out: CsfStatement[] = [];
  for (const row of asArray(raw)) {
    if (!row || typeof row !== 'object') continue;
    const item = row as Record<string, unknown>;
    const text = asText(item.text);
    if (!text) continue;
    out.push({
      scfCode: asText(item.scfCode),
      groupLabel: asText(item.groupLabel),
      text,
      source: asText(item.source),
    });
  }
  return out;
}

function formatFieldAnswer(value: unknown): string | null {
  if (value == null || value === '') return null;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (Array.isArray(value)) {
    const parts = value.map((item) => formatFieldAnswer(item)).filter(Boolean);
    return parts.length ? parts.join(', ') : null;
  }
  if (typeof value === 'object') return null;
  const text = String(value).trim();
  if (!text || /^(no|false|n\/a|none|null|undefined)$/i.test(text)) return null;
  return text;
}

export function freezeFields(raw: unknown): CsfField[] {
  const out: CsfField[] = [];
  const seen = new Set<string>();
  for (const row of asArray(raw)) {
    if (!row || typeof row !== 'object') continue;
    const item = row as Record<string, unknown>;
    const scfCode = asText(item.scfCode);
    const sourceLabel = asText(item.scfLabel) || asText(item.displayLabel);
    const contractLabel = asText(item.contractLabel);
    const scfLabel = paperCsfLabel({ contractLabel, scfLabel: sourceLabel }) || sourceLabel;
    if (!scfCode || !scfLabel || seen.has(scfCode)) continue;
    seen.add(scfCode);
    out.push({
      scfCode,
      scfLabel,
      sourceLabel,
      contractLabel,
      groupLabel: asText(item.groupLabel),
      answerValue: item.answerValue,
      answerDisplay: asText(item.answerDisplay),
      contractVisibility: asText(item.contractVisibility),
    });
  }
  return out;
}

/** Build Label — Value statements from field answers when the engine omitted a line. */
export function statementsFromFields(raw: unknown): CsfStatement[] {
  const out: CsfStatement[] = [];
  for (const field of freezeFields(raw)) {
    if (field.contractVisibility === 'HIDDEN') continue;
    const value = formatFieldAnswer(field.answerDisplay ?? field.answerValue);
    if (!value) continue;
    out.push({
      scfCode: field.scfCode,
      groupLabel: field.groupLabel,
      text: `${field.scfLabel}: ${value}`,
      source: 'field',
    });
  }
  return out;
}

function mergeStatements(...lists: CsfStatement[][]): CsfStatement[] {
  const out: CsfStatement[] = [];
  const seen = new Set<string>();
  for (const list of lists) {
    for (const statement of list) {
      const key = (statement.scfCode || statement.text).toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(statement);
    }
  }
  return out;
}

function mergeBlocks(base: CsfBlock[], extra: CsfBlock[]): CsfBlock[] {
  const byHeading = new Map<string, CsfBlock>();
  for (const block of [...base, ...extra]) {
    const key = block.heading.toLowerCase();
    const existing = byHeading.get(key);
    if (!existing) {
      byHeading.set(key, {
        ...block,
        clusters: block.clusters.map((cluster) => ({
          ...cluster,
          rows: [...cluster.rows],
        })),
      });
      continue;
    }
    for (const cluster of block.clusters) {
      const match = existing.clusters.find(
        (row) => row.groupLabel === cluster.groupLabel,
      );
      if (!match) {
        existing.clusters.push({ ...cluster, rows: [...cluster.rows] });
        continue;
      }
      const seen = new Set(
        match.rows.map((row) => (row.label || row.value).toLowerCase()),
      );
      for (const row of cluster.rows) {
        const rowKey = (row.label || row.value).toLowerCase();
        if (seen.has(rowKey)) continue;
        seen.add(rowKey);
        match.rows.push({ ...row, n: match.rows.length + 1 });
      }
    }
  }
  return [...byHeading.values()].map((block, index) => ({
    ...block,
    n: index + 1,
  }));
}

export function splitStatementLabel(
  text: string,
  fieldLabel: string | null,
  aliases: Array<string | null | undefined> = [],
): { label: string | null; value: string } {
  return splitPaperStatementLabel(text, fieldLabel, aliases);
}

function elementHeading(el: {
  elementTypeDisplayName?: string | null;
  instanceLabel?: string | null;
}): string {
  return (
    [asText(el.elementTypeDisplayName), asText(el.instanceLabel)]
      .filter(Boolean)
      .join(' — ') || 'Scope'
  );
}

function clusterStatements(
  statements: CsfStatement[],
  fields: CsfField[],
): CsfCluster[] {
  const labelByCode = new Map<string, string>();
  const sourceLabelByCode = new Map<string, string>();
  for (const field of fields) {
    if (!labelByCode.has(field.scfCode)) {
      labelByCode.set(field.scfCode, field.scfLabel);
    }
    if (field.sourceLabel && !sourceLabelByCode.has(field.scfCode)) {
      sourceLabelByCode.set(field.scfCode, field.sourceLabel);
    }
  }

  const buckets: Array<{ label: string | null; items: CsfStatement[] }> = [];
  const indexByLabel = new Map<string, number>();
  let otherIndex = -1;
  for (const statement of statements) {
    const group = statement.groupLabel ?? null;
    if (group == null) {
      if (otherIndex < 0) {
        otherIndex = buckets.length;
        buckets.push({ label: null, items: [] });
      }
      buckets[otherIndex].items.push(statement);
      continue;
    }
    let index = indexByLabel.get(group);
    if (index == null) {
      index = buckets.length;
      indexByLabel.set(group, index);
      buckets.push({ label: group, items: [] });
    }
    buckets[index].items.push(statement);
  }
  if (otherIndex >= 0 && otherIndex !== buckets.length - 1) {
    const [other] = buckets.splice(otherIndex, 1);
    buckets.push(other);
  }

  return buckets.map((bucket) => ({
    groupLabel: (bucket.label ?? 'Other').toUpperCase(),
    rows: bucket.items.map((statement, index) => {
      const { label, value } = splitStatementLabel(
        statement.text,
        statement.scfCode ? (labelByCode.get(statement.scfCode) ?? null) : null,
        statement.scfCode ? [sourceLabelByCode.get(statement.scfCode)] : [],
      );
      return { n: index + 1, label, value };
    }),
  }));
}

export function isCsfBlockArray(value: unknown): value is CsfBlock[] {
  return normalizeCsfScope(value).length > 0;
}

function normalizeCsfRow(raw: unknown, index: number): CsfRow | null {
  if (!raw || typeof raw !== 'object') return null;
  const item = raw as Record<string, unknown>;
  const value = asText(item.value) || asText(item.text);
  if (!value) return null;
  const n = typeof item.n === 'number' && Number.isFinite(item.n) ? item.n : index + 1;
  return { n, label: asText(item.label), value };
}

function normalizeCsfCluster(raw: unknown): CsfCluster | null {
  if (!raw || typeof raw !== 'object') return null;
  const item = raw as Record<string, unknown>;
  const groupLabel = (asText(item.groupLabel) || 'OTHER').toUpperCase();
  const rows = asArray(item.rows)
    .map((row, index) => normalizeCsfRow(row, index))
    .filter((row): row is CsfRow => row != null);
  if (rows.length === 0) return null;
  return { groupLabel, rows };
}

/** Server-built csfScope, including empty/malformed nested rows/clusters. */
export function normalizeCsfScope(raw: unknown): CsfBlock[] {
  const out: CsfBlock[] = [];
  for (const [index, block] of asArray(raw).entries()) {
    if (!block || typeof block !== 'object') continue;
    const item = block as Record<string, unknown>;
    const heading = asText(item.heading);
    if (!heading) continue;
    const clusters = asArray(item.clusters)
      .map((cluster) => normalizeCsfCluster(cluster))
      .filter((cluster): cluster is CsfCluster => cluster != null);
    if (clusters.length === 0) continue;
    const n = typeof item.n === 'number' && Number.isFinite(item.n) ? item.n : index + 1;
    out.push({ n, heading, clusters });
  }
  return out;
}

export function isProjectNotesHeading(heading: string): boolean {
  return /^scope notes$/i.test(heading.trim());
}

function notesBlockFromProjectScope(
  projectScope?: { statements?: unknown; fields?: unknown } | null,
): CsfBlock | null {
  const statements = mergeStatements(
    freezeStatements(projectScope?.statements),
    statementsFromFields(projectScope?.fields),
  );
  if (statements.length === 0) return null;
  return {
    n: 1,
    heading: 'Scope Notes',
    clusters: clusterStatements(statements, freezeFields(projectScope?.fields)),
  };
}

export function projectDetailRowsFromBlocks(
  blocks: CsfBlock[],
): Array<[string, string]> {
  const rows: Array<[string, string]> = [];
  for (const block of blocks) {
    if (!isProjectNotesHeading(block.heading)) continue;
    for (const cluster of block.clusters) {
      for (const row of cluster.rows) {
        const value = row.value?.trim();
        if (!value) continue;
        rows.push([row.label?.trim() || cluster.groupLabel, value]);
      }
    }
  }
  return rows;
}

export function elementScopeBlocks(blocks: CsfBlock[]): CsfBlock[] {
  return blocks
    .filter((block) => !isProjectNotesHeading(block.heading))
    .map((block, index) => ({ ...block, n: index + 1 }));
}

export function buildCsfBlocks(input: {
  csfScope?: unknown;
  projectScope?: { statements?: unknown; fields?: unknown } | null;
  scope?: unknown;
}): CsfBlock[] {
  const fromServer = normalizeCsfScope(input.csfScope);
  const serverNotes = fromServer.filter((block) =>
    isProjectNotesHeading(block.heading),
  );
  const serverElements = fromServer.filter(
    (block) => !isProjectNotesHeading(block.heading),
  );

  const notes =
    notesBlockFromProjectScope(input.projectScope) ?? serverNotes[0] ?? null;

  const fromScope: CsfBlock[] = [];
  for (const el of asArray<{
    elementTypeDisplayName?: string | null;
    instanceLabel?: string | null;
    statements?: unknown;
    fields?: unknown;
  }>(input.scope)) {
    const statements = mergeStatements(
      freezeStatements(el?.statements),
      statementsFromFields(el?.fields),
    );
    if (statements.length === 0) continue;
    fromScope.push({
      n: fromScope.length + 1,
      heading: elementHeading(el ?? {}),
      clusters: clusterStatements(statements, freezeFields(el?.fields)),
    });
  }

  return mergeBlocks(
    notes ? [notes, ...serverElements] : serverElements,
    fromScope,
  );
}
